/**
 * Generic multi-select state for explorer panels.
 *
 * Click semantics (modeled after macOS Finder / VS Code file tree):
 * - Plain click       → replace selection with this single item.
 * - Cmd / Ctrl click  → toggle this item in the selection set.
 * - Shift click       → select the contiguous range from the anchor to
 *                       this item, using the supplied `visibleOrder`
 *                       to determine adjacency.
 *
 * The hook does NOT call into any other selection store (e.g. the canvas
 * highlight). The caller decides whether a plain click should also drive
 * a single-selection API — see ConceptExplorer's `handleRowClick` for an
 * example.
 */
import { useCallback, useState } from "react";

export interface MultiSelect {
  selectedIds: Set<string>;
  isSelected: (id: string) => boolean;
  /** Number of currently-selected items. */
  size: number;
  /**
   * Apply a click to `id`. The mouse event provides the modifier keys.
   * `visibleOrder` is the flat render-order of all currently-visible
   * items in the panel — needed for shift-range selection.
   */
  handleClick: (
    id: string,
    e: { metaKey: boolean; ctrlKey: boolean; altKey: boolean; shiftKey: boolean },
    visibleOrder: string[],
  ) => "single" | "multi";
  clear: () => void;
  setOnly: (id: string) => void;
}

export function useMultiSelect(): MultiSelect {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<string | null>(null);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const handleClick = useCallback(
    (
      id: string,
      e: { metaKey: boolean; ctrlKey: boolean; altKey: boolean; shiftKey: boolean },
      visibleOrder: string[],
    ): "single" | "multi" => {
      if (e.shiftKey && anchor && anchor !== id) {
        const a = visibleOrder.indexOf(anchor);
        const b = visibleOrder.indexOf(id);
        if (a >= 0 && b >= 0) {
          const [from, to] = a < b ? [a, b] : [b, a];
          const range = new Set(visibleOrder.slice(from, to + 1));
          setSelectedIds(range);
          return range.size > 1 ? "multi" : "single";
        }
      }
      // Multi-pick toggle: Cmd / Ctrl / Alt all enter multi-mode. Alt
      // is the macOS-friendly fallback when Ctrl-click gets intercepted
      // as secondary-click and Cmd is hijacked by an OS-level shortcut.
      if (e.metaKey || e.ctrlKey || e.altKey) {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
        setAnchor(id);
        return "multi";
      }
      // Plain click → reset to singleton.
      setSelectedIds(new Set([id]));
      setAnchor(id);
      return "single";
    },
    [anchor],
  );

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    setAnchor(null);
  }, []);

  const setOnly = useCallback((id: string) => {
    setSelectedIds(new Set([id]));
    setAnchor(id);
  }, []);

  return {
    selectedIds,
    isSelected,
    size: selectedIds.size,
    handleClick,
    clear,
    setOnly,
  };
}
